import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSortAlphaDown, FaSortAlphaUp } from 'react-icons/fa';
import type { U3129HonorariumProjectSortField, U3129HonorariumSortOrder } from '../../tools/u3129HonorariumGridSort';
import { U3129_HONORARIUM_META_COLUMN_CELL_X } from '../../tools/u3129TimesheetGridCompactStyles';

export type U3129HonorariumMetaSortField = U3129HonorariumProjectSortField | 'task';

export interface U3129HonorariumSortableMetaHeaderProps {
  ns: string;
  labelKey: string;
  labelDefault: string;
  colClass: string;
  sortField: U3129HonorariumMetaSortField;
  activeProjectField: U3129HonorariumProjectSortField;
  activeProjectOrder: U3129HonorariumSortOrder;
  activeTaskOrder: U3129HonorariumSortOrder;
  taskSortActive: boolean;
  onSortProject: (field: U3129HonorariumProjectSortField) => void;
  onSortTask: () => void;
}

const U3129HonorariumSortableMetaHeader: React.FC<U3129HonorariumSortableMetaHeaderProps> = ({
  ns,
  labelKey,
  labelDefault,
  colClass,
  sortField,
  activeProjectField,
  activeProjectOrder,
  activeTaskOrder,
  taskSortActive,
  onSortProject,
  onSortTask,
}) => {
  const { t } = useTranslation();

  const isProjectSort = sortField === 'project' || sortField === 'customer' || sortField === 'natures';
  const isActive = isProjectSort ? activeProjectField === sortField : taskSortActive;
  const order = isProjectSort ? activeProjectOrder : activeTaskOrder;
  const ariaSort = isActive ? (order === 'ASC' ? 'ascending' : 'descending') : 'none';
  const label = t(labelKey, { ns, defaultValue: labelDefault });

  const sortHint = isActive
    ? order === 'ASC'
      ? t('ariaSortColumnDescending', { ns, column: label, defaultValue: `Trier par ${label}, ordre décroissant` })
      : t('ariaSortColumnAscending', { ns, column: label, defaultValue: `Trier par ${label}, ordre croissant` })
    : t('ariaSortColumnActivate', { ns, column: label, defaultValue: `Trier par ${label}` });

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (sortField === 'task') {
          onSortTask();
        } else if (sortField !== 'default') {
          onSortProject(sortField);
        }
      }
    },
    [onSortProject, onSortTask, sortField],
  );

  const handleClick = () => {
    if (sortField === 'task') {
      onSortTask();
      return;
    }
    if (sortField === 'project' || sortField === 'customer' || sortField === 'natures') {
      onSortProject(sortField);
    }
  };

  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      title={sortHint}
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={`${U3129_HONORARIUM_META_COLUMN_CELL_X} py-2.5 text-left font-medium text-gray-700 cursor-pointer select-none hover:bg-gray-200/50 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset ${colClass}`}
    >
      <span className="inline-flex max-w-full items-center gap-1">
        <span className="truncate">{label}</span>
        {isActive && order === 'ASC' ? (
          <FaSortAlphaDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
        ) : null}
        {isActive && order === 'DESC' ? (
          <FaSortAlphaUp className="h-3.5 w-3.5 shrink-0" aria-hidden />
        ) : null}
      </span>
    </th>
  );
};

export default U3129HonorariumSortableMetaHeader;
